import { LendApiCombinedPrequalTab, LendApiCombinedFullAppTab } from '../constants';
import { LendAPIProgressConfig, ValuePropConfig } from './prequalProgressConfig';

type CombinedPhase = 'prequal' | 'fullApp';

const COMBINED_PREQUAL_RESULTS_TABS: LendApiCombinedPrequalTab[] = [
  LendApiCombinedPrequalTab.RESULTS_NO_HIT_SOFT_PULL,
  LendApiCombinedPrequalTab.RESULTS_NO_OFFERS,
  LendApiCombinedPrequalTab.RESULTS_MANUAL_REVIEW_NO_OFFERS,
  LendApiCombinedPrequalTab.APPROVAL_ODDS_EXCELLENT,
  LendApiCombinedPrequalTab.APPROVAL_ODDS_VERY_GOOD,
  LendApiCombinedPrequalTab.APPROVAL_ODDS_GOOD,
  LendApiCombinedPrequalTab.APPROVAL_ODDS_FAIR,
  LendApiCombinedPrequalTab.APPROVAL_ODDS_POINTS,
  LendApiCombinedPrequalTab.APPROVAL_ODDS_MANUAL_REVIEW,
  LendApiCombinedPrequalTab.APPROVAL_ODDS_NO_OFFERS,
];

const COMBINED_PREQUAL_TABS: Set<string> = new Set([
  LendApiCombinedPrequalTab.LOAN_PURPOSE_NA,
  LendApiCombinedPrequalTab.LETS_GET_STARTED_NA,
  LendApiCombinedPrequalTab.BOAT_DETAILS,
  LendApiCombinedPrequalTab.ABOUT_YOU_INDIVIDUAL,
  LendApiCombinedPrequalTab.ABOUT_YOU_JOINT,
  LendApiCombinedPrequalTab.SSN_SOFT_PULL,
  LendApiCombinedPrequalTab.SSN_SOFT_PULL_JOINT,
  ...COMBINED_PREQUAL_RESULTS_TABS,
]);

const COMBINED_FULLAPP_TABS: Set<string> = new Set([
  LendApiCombinedFullAppTab.PREVIOUS_ADDRESS,
  LendApiCombinedFullAppTab.CURRENT_ADDRESS,
  LendApiCombinedFullAppTab.PRIMARY_PROFILE_NA,
  LendApiCombinedFullAppTab.PROFILE_COBORROWER,
  LendApiCombinedFullAppTab.MAILING_EQUALS_CURRENT,
  LendApiCombinedFullAppTab.MAILING_ADDRESS,
  LendApiCombinedFullAppTab.CO_CURRENT_EQUALS_PRIMARY,
  LendApiCombinedFullAppTab.ADDRESS_COBORROWER,
  LendApiCombinedFullAppTab.PREVIOUS_ADDRESS_COBORROWER,
  LendApiCombinedFullAppTab.BOAT_INFO_TRADE_IN,
  LendApiCombinedFullAppTab.LOAN_INFO,
  LendApiCombinedFullAppTab.INCOME,
  LendApiCombinedFullAppTab.INCOME_EMPLOYMENT,
  LendApiCombinedFullAppTab.INCOME_LIQUID_RETIREMENT,
  LendApiCombinedFullAppTab.INCOME_REAL_ESTATE,
  LendApiCombinedFullAppTab.INCOME_OTHER_ASSETS,
  LendApiCombinedFullAppTab.COBORROWER_INCOME,
  LendApiCombinedFullAppTab.COBORROWER_EMPLOYMENT,
  LendApiCombinedFullAppTab.COBORROWER_LIQUID_RETIREMENT,
  LendApiCombinedFullAppTab.COBORROWER_REAL_ESTATE,
  LendApiCombinedFullAppTab.COBORROWER_OTHER_ASSETS,
  LendApiCombinedFullAppTab.PRIMARY_EMPLOYMENT,
  LendApiCombinedFullAppTab.PRIMARY_LIQUID_RETIREMENT,
  LendApiCombinedFullAppTab.PRIMARY_REAL_ESTATE,
  LendApiCombinedFullAppTab.PRIMARY_OTHER_ASSETS,
  LendApiCombinedFullAppTab.JD_POWER,
  LendApiCombinedFullAppTab.CREDIT_CONSENT,
  LendApiCombinedFullAppTab.CREDIT_CONSENT_PRIMARY_SSN,
  LendApiCombinedFullAppTab.SUBMITTED,
]);

const PAGE_1_LETS_GET_STARTED: ValuePropConfig = {
  items: [
    { text: 'Trusted by 10,000+ customers in the last year.' },
    { text: 'Funded $200+ million in loan amounts.' },
    { text: 'No impact to your credit score.' },
  ],
  primaryIcon: 'boat',
  title: "Let's get you on the water",
};

const PAGE_2_BOAT_DETAILS: ValuePropConfig = {
  items: [
    { text: 'Funded $200+ million in loan amounts.' },
    { text: 'Everything is aligned upfront for a smooth buying process.' },
    { text: 'No impact to your credit score.' },
  ],
  primaryIcon: 'dollarSign',
  title: 'Your one-stop shop for boat loans',
};

const PAGE_3_ABOUT_YOU: ValuePropConfig = {
  items: [
    { text: 'Trusted by 10,000+ customers in the last year.' },
    { text: 'Submit your info and get matched with a network of lenders.' },
    { text: 'No impact to your credit score.' },
  ],
  primaryIcon: 'shield',
  title: 'A smarter, faster way to finance your boat',
};

const PAGE_4_SSN: ValuePropConfig = {
  items: [
    { text: 'Providing your SSN ensures we match you correctly.' },
    { text: 'Your SSN is protected with encryption and used strictly for this application.' },
    { text: 'No impact to your credit score.' },
  ],
  primaryIcon: 'shield',
  title: 'Your security is our top priority',
};

const PAGE_6_CURRENT_ADDRESS: ValuePropConfig = {
  items: [
    { text: 'Complete details now mean fewer delays later.' },
    { text: 'Your address ensures accurate matching and prevents fraud.' },
    {
      text: 'We match you with a network of lenders to help you find the best possible terms.',
    },
  ],
  primaryIcon: 'shield',
  title: 'A smarter, faster way to finance your boat',
};

const PAGE_7_MAILING_ADDRESS: ValuePropConfig = {
  items: [
    { text: 'Ensures your loan details and updates reach you without issue.' },
    { text: 'Choose what works best for receiving important documents.' },
    {
      text: 'We match you with a network of lenders to help you find the best possible terms.',
    },
  ],
  primaryIcon: 'shield',
  title: 'A smarter, faster way to finance your boat',
};

const PAGE_8_LOAN_INFO: ValuePropConfig = {
  items: [
    { text: 'Select the structure that best matches your budget and goals.' },
    { text: 'Bundle services now to simplify your financing later.' },
    {
      text: 'We match you with a network of lenders to help you find the best possible terms.',
    },
  ],
  primaryIcon: 'dollarSign',
  title: 'Your one-stop shop for boat loans',
};

const PAGE_8_1_TRADE_IN: ValuePropConfig = {
  items: [
    { text: 'Just basic details to estimate your value.' },
    { text: 'Turn your existing asset into buying power.' },
    {
      text: 'We match you with a network of lenders to help you find the best possible terms.',
    },
  ],
  primaryIcon: 'dollarSign',
  title: 'Your one-stop shop for boat loans',
};

const PAGE_9_INCOME: ValuePropConfig = {
  items: [
    { text: 'Your financial details are protected at every step.' },
    { text: 'Accurate income information ensures you get the best possible offer.' },
    {
      text: 'We match you with a network of lenders to help you find the best possible terms.',
    },
  ],
  primaryIcon: 'shield',
  title: 'Your security is our top priority',
};

const PAGE_10_EMPLOYMENT: ValuePropConfig = {
  items: [
    { text: 'Employment history strengthens your application.' },
    { text: 'Accurate employment information ensures you get the best possible offer.' },
    {
      text: 'We match you with a network of lenders to help you find the best possible terms.',
    },
  ],
  primaryIcon: 'shield',
  title: 'Your security is our top priority',
};

const PAGE_11_JD_POWER: ValuePropConfig = {
  items: [
    { text: "You're nearly done — just confirm your selection." },
    { text: 'Everything is aligned upfront for a smooth buying process.' },
    { text: 'Final details ensure your offer matches your purchase perfectly.' },
  ],
  primaryIcon: 'medal',
  title: "You're nearly there",
};

const PAGE_12_CREDIT_CONSENT: ValuePropConfig = {
  items: [
    { text: 'Trusted by 10,000+ customers in the last year.' },
    { text: 'Submit knowing exactly what to expect next — no surprises.' },
    {
      text: 'We match you with a network of lenders to help you find the best possible terms.',
    },
  ],
  primaryIcon: 'shield',
  title: 'Your one-stop shop for boat loans',
};

const DEFAULT_VALUE_PROP: ValuePropConfig = {
  items: [
    { text: 'Trusted by 10,000+ customers in the last year.' },
    { text: 'Funded $200+ million in loan amounts.' },
    {
      text: 'We match you with a network of lenders to help you find the best possible terms.',
    },
  ],
  primaryIcon: 'dollarSign',
  title: 'Your one-stop shop for boat loans',
};

export const COMBINED_PREQUAL_PROGRESS_CONFIG: LendAPIProgressConfig<string> = {
  milestones: [
    {
      name: 'Boat Info',
      tabs: new Set([
        LendApiCombinedPrequalTab.LOAN_PURPOSE_NA,
        LendApiCombinedPrequalTab.LETS_GET_STARTED_NA,
        LendApiCombinedPrequalTab.BOAT_DETAILS,
      ]),
    },
    {
      name: 'About You',
      tabs: new Set([
        LendApiCombinedPrequalTab.ABOUT_YOU_INDIVIDUAL,
        LendApiCombinedPrequalTab.ABOUT_YOU_JOINT,
        LendApiCombinedPrequalTab.SSN_SOFT_PULL,
        LendApiCombinedPrequalTab.SSN_SOFT_PULL_JOINT,
      ]),
    },
    {
      name: 'Pre-Qual Results',
      tabs: new Set(COMBINED_PREQUAL_RESULTS_TABS),
    },
  ],
  tabLookup: new Map([
    [LendApiCombinedPrequalTab.LOAN_PURPOSE_NA, { milestoneIndex: 0, isStartOfGroup: true }],
    [
      LendApiCombinedPrequalTab.LETS_GET_STARTED_NA,
      { milestoneIndex: 0, isStartOfGroup: false, valueProp: PAGE_1_LETS_GET_STARTED },
    ],
    [
      LendApiCombinedPrequalTab.BOAT_DETAILS,
      { milestoneIndex: 0, isStartOfGroup: true, valueProp: PAGE_2_BOAT_DETAILS },
    ],
    [
      LendApiCombinedPrequalTab.ABOUT_YOU_INDIVIDUAL,
      { milestoneIndex: 1, isStartOfGroup: true, valueProp: PAGE_3_ABOUT_YOU },
    ],
    [
      LendApiCombinedPrequalTab.ABOUT_YOU_JOINT,
      { milestoneIndex: 1, isStartOfGroup: true, valueProp: PAGE_3_ABOUT_YOU },
    ],
    [
      LendApiCombinedPrequalTab.SSN_SOFT_PULL,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: PAGE_4_SSN },
    ],
    [
      LendApiCombinedPrequalTab.SSN_SOFT_PULL_JOINT,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: PAGE_4_SSN },
    ],
    [
      LendApiCombinedPrequalTab.RESULTS_NO_HIT_SOFT_PULL,
      { milestoneIndex: 2, isStartOfGroup: true },
    ],
    [LendApiCombinedPrequalTab.RESULTS_NO_OFFERS, { milestoneIndex: 2, isStartOfGroup: true }],
    [
      LendApiCombinedPrequalTab.RESULTS_MANUAL_REVIEW_NO_OFFERS,
      { milestoneIndex: 2, isStartOfGroup: true },
    ],
    [
      LendApiCombinedPrequalTab.APPROVAL_ODDS_EXCELLENT,
      { milestoneIndex: 2, isStartOfGroup: true },
    ],
    [
      LendApiCombinedPrequalTab.APPROVAL_ODDS_VERY_GOOD,
      { milestoneIndex: 2, isStartOfGroup: true },
    ],
    [LendApiCombinedPrequalTab.APPROVAL_ODDS_GOOD, { milestoneIndex: 2, isStartOfGroup: true }],
    [LendApiCombinedPrequalTab.APPROVAL_ODDS_FAIR, { milestoneIndex: 2, isStartOfGroup: true }],
    [LendApiCombinedPrequalTab.APPROVAL_ODDS_POINTS, { milestoneIndex: 2, isStartOfGroup: true }],
    [
      LendApiCombinedPrequalTab.APPROVAL_ODDS_MANUAL_REVIEW,
      { milestoneIndex: 2, isStartOfGroup: true },
    ],
    [
      LendApiCombinedPrequalTab.APPROVAL_ODDS_NO_OFFERS,
      { milestoneIndex: 2, isStartOfGroup: true },
    ],
  ]),
  tabsWithNoValueProp: new Set(COMBINED_PREQUAL_RESULTS_TABS),
  defaultValueProp: DEFAULT_VALUE_PROP,
};

export const COMBINED_FULLAPP_PROGRESS_CONFIG: LendAPIProgressConfig<string> = {
  milestones: [
    {
      name: 'About You',
      tabs: new Set([
        LendApiCombinedFullAppTab.PREVIOUS_ADDRESS,
        LendApiCombinedFullAppTab.CURRENT_ADDRESS,
        LendApiCombinedFullAppTab.PRIMARY_PROFILE_NA,
        LendApiCombinedFullAppTab.PROFILE_COBORROWER,
        LendApiCombinedFullAppTab.MAILING_EQUALS_CURRENT,
        LendApiCombinedFullAppTab.MAILING_ADDRESS,
        LendApiCombinedFullAppTab.CO_CURRENT_EQUALS_PRIMARY,
        LendApiCombinedFullAppTab.ADDRESS_COBORROWER,
        LendApiCombinedFullAppTab.PREVIOUS_ADDRESS_COBORROWER,
      ]),
    },
    {
      name: 'Financing Details',
      tabs: new Set([
        LendApiCombinedFullAppTab.BOAT_INFO_TRADE_IN,
        LendApiCombinedFullAppTab.LOAN_INFO,
        LendApiCombinedFullAppTab.INCOME,
        LendApiCombinedFullAppTab.INCOME_EMPLOYMENT,
        LendApiCombinedFullAppTab.INCOME_LIQUID_RETIREMENT,
        LendApiCombinedFullAppTab.INCOME_REAL_ESTATE,
        LendApiCombinedFullAppTab.INCOME_OTHER_ASSETS,
        LendApiCombinedFullAppTab.COBORROWER_INCOME,
        LendApiCombinedFullAppTab.COBORROWER_EMPLOYMENT,
        LendApiCombinedFullAppTab.COBORROWER_LIQUID_RETIREMENT,
        LendApiCombinedFullAppTab.COBORROWER_REAL_ESTATE,
        LendApiCombinedFullAppTab.COBORROWER_OTHER_ASSETS,
        LendApiCombinedFullAppTab.PRIMARY_EMPLOYMENT,
        LendApiCombinedFullAppTab.PRIMARY_LIQUID_RETIREMENT,
        LendApiCombinedFullAppTab.PRIMARY_REAL_ESTATE,
        LendApiCombinedFullAppTab.PRIMARY_OTHER_ASSETS,
      ]),
    },
    {
      name: 'Boat Info',
      tabs: new Set([LendApiCombinedFullAppTab.JD_POWER]),
    },
    {
      name: 'Confirm',
      tabs: new Set([
        LendApiCombinedFullAppTab.CREDIT_CONSENT,
        LendApiCombinedFullAppTab.CREDIT_CONSENT_PRIMARY_SSN,
        LendApiCombinedFullAppTab.SUBMITTED,
      ]),
    },
  ],
  tabLookup: new Map([
    [LendApiCombinedFullAppTab.PREVIOUS_ADDRESS, { milestoneIndex: 0, isStartOfGroup: true }],
    [
      LendApiCombinedFullAppTab.CURRENT_ADDRESS,
      { milestoneIndex: 0, isStartOfGroup: false, valueProp: PAGE_6_CURRENT_ADDRESS },
    ],
    [LendApiCombinedFullAppTab.PRIMARY_PROFILE_NA, { milestoneIndex: 0, isStartOfGroup: false }],
    [LendApiCombinedFullAppTab.PROFILE_COBORROWER, { milestoneIndex: 0, isStartOfGroup: false }],
    [
      LendApiCombinedFullAppTab.MAILING_EQUALS_CURRENT,
      { milestoneIndex: 0, isStartOfGroup: false, valueProp: PAGE_7_MAILING_ADDRESS },
    ],
    [
      LendApiCombinedFullAppTab.MAILING_ADDRESS,
      { milestoneIndex: 0, isStartOfGroup: false, valueProp: PAGE_7_MAILING_ADDRESS },
    ],
    [
      LendApiCombinedFullAppTab.CO_CURRENT_EQUALS_PRIMARY,
      { milestoneIndex: 0, isStartOfGroup: false, valueProp: PAGE_6_CURRENT_ADDRESS },
    ],
    [
      LendApiCombinedFullAppTab.ADDRESS_COBORROWER,
      { milestoneIndex: 0, isStartOfGroup: false, valueProp: PAGE_6_CURRENT_ADDRESS },
    ],
    [
      LendApiCombinedFullAppTab.PREVIOUS_ADDRESS_COBORROWER,
      { milestoneIndex: 0, isStartOfGroup: false },
    ],
    [
      LendApiCombinedFullAppTab.BOAT_INFO_TRADE_IN,
      { milestoneIndex: 1, isStartOfGroup: true, valueProp: PAGE_8_1_TRADE_IN },
    ],
    [
      LendApiCombinedFullAppTab.LOAN_INFO,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: PAGE_8_LOAN_INFO },
    ],
    [
      LendApiCombinedFullAppTab.INCOME,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: PAGE_9_INCOME },
    ],
    [
      LendApiCombinedFullAppTab.INCOME_EMPLOYMENT,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: PAGE_10_EMPLOYMENT },
    ],
    [
      LendApiCombinedFullAppTab.INCOME_LIQUID_RETIREMENT,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: PAGE_9_INCOME },
    ],
    [
      LendApiCombinedFullAppTab.INCOME_REAL_ESTATE,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: PAGE_9_INCOME },
    ],
    [
      LendApiCombinedFullAppTab.INCOME_OTHER_ASSETS,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: PAGE_9_INCOME },
    ],
    [
      LendApiCombinedFullAppTab.COBORROWER_INCOME,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: PAGE_9_INCOME },
    ],
    [
      LendApiCombinedFullAppTab.COBORROWER_EMPLOYMENT,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: PAGE_10_EMPLOYMENT },
    ],
    [
      LendApiCombinedFullAppTab.COBORROWER_LIQUID_RETIREMENT,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: PAGE_9_INCOME },
    ],
    [
      LendApiCombinedFullAppTab.COBORROWER_REAL_ESTATE,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: PAGE_9_INCOME },
    ],
    [
      LendApiCombinedFullAppTab.COBORROWER_OTHER_ASSETS,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: PAGE_9_INCOME },
    ],
    [
      LendApiCombinedFullAppTab.PRIMARY_EMPLOYMENT,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: PAGE_10_EMPLOYMENT },
    ],
    [
      LendApiCombinedFullAppTab.PRIMARY_LIQUID_RETIREMENT,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: PAGE_9_INCOME },
    ],
    [
      LendApiCombinedFullAppTab.PRIMARY_REAL_ESTATE,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: PAGE_9_INCOME },
    ],
    [
      LendApiCombinedFullAppTab.PRIMARY_OTHER_ASSETS,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: PAGE_9_INCOME },
    ],
    [
      LendApiCombinedFullAppTab.JD_POWER,
      { milestoneIndex: 2, isStartOfGroup: true, valueProp: PAGE_11_JD_POWER },
    ],
    [
      LendApiCombinedFullAppTab.CREDIT_CONSENT,
      { milestoneIndex: 3, isStartOfGroup: true, valueProp: PAGE_12_CREDIT_CONSENT },
    ],
    [
      LendApiCombinedFullAppTab.CREDIT_CONSENT_PRIMARY_SSN,
      { milestoneIndex: 3, isStartOfGroup: true, valueProp: PAGE_12_CREDIT_CONSENT },
    ],
    [LendApiCombinedFullAppTab.SUBMITTED, { milestoneIndex: 3, isStartOfGroup: false }],
  ]),
  tabsWithNoValueProp: new Set([LendApiCombinedFullAppTab.SUBMITTED]),
  defaultValueProp: DEFAULT_VALUE_PROP,
};

export const getCombinedProgressPhase = (tabName: string): CombinedPhase | null => {
  if (COMBINED_PREQUAL_TABS.has(tabName) && !COMBINED_FULLAPP_TABS.has(tabName)) {
    return 'prequal';
  }
  if (COMBINED_FULLAPP_TABS.has(tabName)) {
    return 'fullApp';
  }
  return null;
};

const COMBINED_APPLICATION_PORTION_TABS: Set<string> = new Set([
  ...COMBINED_PREQUAL_RESULTS_TABS,
  ...Array.from(COMBINED_FULLAPP_TABS),
]);

export const isCombinedApplicationPortionTab = (tabName: string): boolean =>
  COMBINED_APPLICATION_PORTION_TABS.has(tabName);
